import { cn, iaRa, io } from 'lib/helpers/utility';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useIsShellHosted } from 'lib/context/ShellContext/ShellContext';

import Export from 'components/shared/Export/Export';
import Filters from 'components/shared/Filters/Filters';
import FilterActions from 'components/shared/Filters/components/FilterActions';
import { hasActiveFilters } from 'components/shared/Filters/lib/hasActiveFilters';
import DisplayButtonAGTable from 'components/shared/AGTable/components/DisplayButtonAGTable/DisplayButtonAGTable';

import { buildExportData } from '../lib/helpers';
import { VIEW_FILTER_KEY } from '../lib/createQueryFilters';

import SearchInput from './SearchInput';

const TableFilters = ({
  filters,
  initialFilters,
  changeFilters,
  updateFilters,
  category,
  defaultFilters,
  isEditingFilter,
  className = '',
  disableActions = false,
  items = [],
  type,
  noDisplayButton = false,
  noFilterActions = false,
  alwaysShowFilters: alwaysShowFiltersProp,
  quickFilters,
  modalView,
  hasData = true,
  itemsCount = 0,
  showExport = false,
  exportStatusPanelData = [],
  exportData,
  getExportData
}) => {
  const tableContext = useTableContext();
  const isHosted = useIsShellHosted();
  const alwaysShowFilters = alwaysShowFiltersProp ?? isHosted;
  const { view, isSwitchItemView } = tableContext;
  const pinnedKeys = isSwitchItemView ? [VIEW_FILTER_KEY] : [];
  const quickKeys = typeof quickFilters === 'function' ? quickFilters(view) : quickFilters;
  // Status tabs already are the status filter; a second Status chip beside them would compete.
  // Tabs that go to another page (`navigate`) set no status, so they leave Status alone.
  const notQuick = iaRa(items).some((item) => !item.navigate) ? [VIEW_FILTER_KEY] : [];
  const hasFiltersAndData = io(defaultFilters) && (alwaysShowFilters || hasData);

  const lessThan10AndNoActive =
    io(defaultFilters) && itemsCount > 0 && itemsCount < 10 && !hasActiveFilters(filters);
  const noFiltersAndNoData = !io(defaultFilters) && !hasData;

  const showFilters =
    hasFiltersAndData && (alwaysShowFilters || (!lessThan10AndNoActive && !noFiltersAndNoData));

  const showDisplayButton = !noDisplayButton;
  const showFilterActions = !noFilterActions && !!filters && hasFiltersAndData;

  if (!showFilters && !showDisplayButton && !showFilterActions) {
    return null;
  }

  return (
    <div
      className={cn(
        'outline-b flex min-h-10 items-start justify-between gap-2 bg-white px-4 py-2 outline-1 outline-offset-[-1px] outline-neutral-200',
        className
      )}>
      {showFilters ? (
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          <SearchInput
            defaultFilters={defaultFilters}
            filters={filters}
            changeFilters={changeFilters}
            updateFilters={updateFilters}
          />
          <Filters
            suggested
            quickKeys={quickKeys}
            notQuick={notQuick}
            hiddenKeys={pinnedKeys}
            defaultFilters={defaultFilters}
            filters={filters}
            changeFilters={changeFilters}
            updateFilters={updateFilters}
          />
        </div>
      ) : (
        <div />
      )}
      <div className="flex flex-wrap items-start justify-end gap-2">
        {showExport && hasFiltersAndData && (
          <Export
            showLabel
            customLabel="Export View"
            useData={() =>
              buildExportData({
                tableContext,
                category,
                getExportData,
                data: exportData,
                statusPanelData: exportStatusPanelData
              })
            }
          />
        )}
        {showDisplayButton && <DisplayButtonAGTable />}
        {showFilterActions && (
          <FilterActions
            filters={filters}
            initialFilters={initialFilters}
            changeFilters={changeFilters}
            category={category}
            isEditingFilter={isEditingFilter}
            disableActions={disableActions || modalView}
            items={items}
            type={type}
          />
        )}
      </div>
    </div>
  );
};

export default TableFilters;
